import React, { useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Hotel, FileText, Wind, Ship, Wrench, ArrowRight, CheckCircle2 } from 'lucide-react';
const industries = [{
  id: 'hospitality',
  name: 'Hospitality',
  icon: Hotel,
  painPoints: ['Manual reservations', 'Slow guest response', 'Missed upsell opportunities'],
  workflows: ['Reservation management', 'Guest messaging', 'Service recovery', 'Intelligent upsell'],
  value: ['Faster response times', 'Higher guest satisfaction', 'Lower admin load', 'Better revenue capture'],
  bgClass: 'bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-yellow-900/10 via-transparent to-transparent'
}, {
  id: 'conveyancing',
  name: 'Conveyancing',
  icon: FileText,
  painPoints: ['Document bottlenecks', 'Client anxiety and endless status checks', 'Manual settlement preparation'],
  workflows: ['Document workflow automation', 'Proactive client updates', 'Settlement preparation', 'Compliance checking'],
  value: ['60% less admin time', 'Reduced client anxiety', 'Faster settlements', 'Zero compliance drift'],
  bgClass: 'bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]'
}, {
  id: 'renewables',
  name: 'Renewables',
  icon: Wind,
  painPoints: ['Reactive maintenance', 'Slow fault response', 'Dispatch complexity'],
  workflows: ['Predictive asset maintenance', 'Automated fault triage', 'Smart technician dispatch', 'Performance reporting'],
  value: ['35% reduction in downtime', 'Optimised dispatch routing', 'Higher asset yield', 'Automated compliance logs'],
  bgClass: 'bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-green-900/10 via-transparent to-transparent'
}, {
  id: 'maritime',
  name: 'Maritime',
  icon: Ship,
  painPoints: ['Fragmented fleet data', 'Manual reporting overhead', 'Complex compliance burden'],
  workflows: ['Fleet coordination', 'Automated vessel reporting', 'Compliance workflow management', 'Port arrival orchestration'],
  value: ['Real-time fleet intelligence', 'Zero-touch reporting', 'Reduced port delays', 'Audit-ready compliance'],
  bgClass: 'bg-[radial-gradient(ellipse_at_bottom,_var(--tw-gradient-stops))] from-blue-900/10 via-transparent to-transparent'
}, {
  id: 'field-service',
  name: 'Field Service',
  icon: Wrench,
  painPoints: ['Inefficient dispatching', 'Paper-based work orders', 'Technician downtime'],
  workflows: ['Intelligent dispatch', 'Digital work order automation', 'Technician enablement', 'Parts inventory checking'],
  value: ['25% more jobs completed', 'First-time fix rate increase', 'Reduced travel time', 'Happier technicians'],
  bgClass: 'bg-[radial-gradient(ellipse_at_left,_var(--tw-gradient-stops))] from-orange-900/10 via-transparent to-transparent'
}];
const workflowSteps = ['Input received', 'Context analysed', 'Rules applied', 'Action executed', 'Logged + escalated'];
export function VerticalSolutions() {
  const [activeTab, setActiveTab] = useState(industries[0].id);
  const activeIndustry = industries.find(i => i.id === activeTab) || industries[0];
  return <section id="solutions" className="py-32 bg-[#050505] relative z-10 overflow-hidden" data-id="element-515">
      <div className="max-w-7xl mx-auto px-6" data-id="element-516">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1]
      }} className="text-center mb-16" data-id="element-517">
          <h2 className="font-display text-4xl md:text-5xl text-white mb-6 tracking-tight" data-id="element-518">
            Intelligence Designed Around <br className="hidden md:block" data-id="element-519" />
            <span className="text-gray-500" data-id="element-520">Your Industry.</span>
          </h2>
          <p className="font-body text-lg text-gray-400 max-w-2xl mx-auto" data-id="element-521">
            Pre-configured AI agents tailored to the workflows, pain points, and
            operating realities of your sector.
          </p>
        </motion.div>

        {/* Tab Selector */}
        <div className="flex flex-wrap justify-center gap-3 mb-16" data-id="element-522">
          {industries.map(industry => <button key={industry.id} onClick={() => setActiveTab(industry.id)} className={`relative px-6 py-3 rounded-full text-sm font-medium transition-colors duration-300 ${activeTab === industry.id ? 'text-white' : 'text-gray-400 hover:text-gray-200 bg-[#111] border border-white/5 hover:border-white/10'}`} data-id="element-523">
              {activeTab === industry.id && <motion.div layoutId="activeTab" className="absolute inset-0 bg-brand-orange rounded-full" transition={{
            type: 'spring',
            bounce: 0.2,
            duration: 0.6
          }} data-id="element-524" />}
              <span className="relative z-10 flex items-center gap-2" data-id="element-525">
                <industry.icon className="w-4 h-4" data-id="element-526" />
                {industry.name}
              </span>
            </button>)}
        </div>

        {/* Content Card */}
        <div className="max-w-5xl mx-auto" data-id="element-527">
          <AnimatePresence mode="wait" data-id="element-528">
            <motion.div key={activeTab} initial={{
            opacity: 0,
            y: 20,
            scale: 0.98
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: -20,
            scale: 0.98
          }} transition={{
            duration: 0.5,
            ease: 'easeOut'
          }} className={`bg-[#0A0A0A] border border-white/10 rounded-3xl p-8 md:p-12 overflow-hidden relative group shadow-2xl ${activeIndustry.bgClass}`} data-id="element-529">
              {/* Subtle background glow */}
              <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-brand-orange/5 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/3 pointer-events-none transition-opacity duration-700" data-id="element-530" />

              <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12" data-id="element-531">
                {/* Left Column */}
                <div className="lg:col-span-7" data-id="element-532">
                  <div className="flex items-center gap-4 mb-10" data-id="element-533">
                    <div className="w-14 h-14 bg-[#111] border border-white/10 rounded-2xl flex items-center justify-center shadow-inner" data-id="element-534">
                      <activeIndustry.icon className="w-6 h-6 text-brand-orange" data-id="element-535" />
                    </div>
                    <h3 className="text-3xl font-display text-white tracking-tight" data-id="element-536">
                      Aijentik {activeIndustry.name}
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-537">
                    <div data-id="element-538">
                      <h4 className="text-xs font-medium text-gray-500 uppercase tracking-widest mb-5" data-id="element-539">
                        The Challenge
                      </h4>
                      <ul className="space-y-4" data-id="element-540">
                        {activeIndustry.painPoints.map((point, i) => <li key={i} className="flex items-start gap-3 text-sm text-gray-400" data-id="element-541">
                            <div className="w-1.5 h-1.5 rounded-full bg-gray-600 mt-1.5 flex-shrink-0" data-id="element-542" />
                            {point}
                          </li>)}
                      </ul>
                    </div>

                    <div data-id="element-543">
                      <h4 className="text-xs font-medium text-brand-orange uppercase tracking-widest mb-5" data-id="element-544">
                        AI Workflows Automated
                      </h4>
                      <ul className="space-y-4" data-id="element-545">
                        {activeIndustry.workflows.map((workflow, i) => <li key={i} className="flex items-start gap-3 text-sm text-gray-200 font-medium" data-id="element-546">
                            <div className="w-1.5 h-1.5 rounded-full bg-brand-orange mt-1.5 flex-shrink-0 shadow-[0_0_8px_rgba(219,127,74,0.8)]" data-id="element-547" />
                            {workflow}
                          </li>)}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Right Column - Value Panel */}
                <div className="lg:col-span-5 flex flex-col justify-center" data-id="element-548">
                  <div className="bg-[#111]/80 backdrop-blur-sm rounded-2xl p-8 border border-white/5 relative overflow-hidden group-hover:border-white/10 transition-colors duration-500" data-id="element-549">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-brand-orange to-transparent opacity-50" data-id="element-550" />

                    <h4 className="text-xs font-medium text-gray-500 uppercase tracking-widest mb-6" data-id="element-551">
                      Value Delivered
                    </h4>

                    <ul className="space-y-5" data-id="element-552">
                      {activeIndustry.value.map((val, i) => <li key={i} className="flex items-center gap-3" data-id="element-553">
                          <CheckCircle2 className="w-4 h-4 text-brand-orange flex-shrink-0" data-id="element-554" />
                          <span className="text-white text-sm md:text-base" data-id="element-555">
                            {val}
                          </span>
                        </li>)}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Workflow Preview Timeline */}
              <div className="relative z-10 pt-8 border-t border-white/5" data-id="element-556">
                <h4 className="text-xs font-medium text-gray-500 uppercase tracking-widest mb-6 text-center md:text-left" data-id="element-557">
                  Workflow Execution Preview
                </h4>

                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-0 relative" data-id="element-558">
                  {/* Connecting line (desktop) */}
                  <div className="hidden md:block absolute top-3 left-4 right-4 h-px bg-white/10 z-0" data-id="element-559" />

                  {workflowSteps.map((step, index) => <motion.div key={index} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.4,
                  delay: 0.2 + index * 0.1
                }} className="flex md:flex-col items-center gap-3 md:gap-4 relative z-10 w-full md:w-auto" data-id="element-560">
                      <div className="w-6 h-6 rounded-full bg-[#111] border border-brand-orange/50 flex items-center justify-center flex-shrink-0" data-id="element-561">
                        <motion.div initial={{
                      scale: 0
                    }} animate={{
                      scale: 1
                    }} transition={{
                      duration: 0.3,
                      delay: 0.4 + index * 0.1
                    }} className="w-2 h-2 rounded-full bg-brand-orange" data-id="element-562" />
                      </div>
                      <span className="text-xs text-gray-400 font-medium md:text-center whitespace-nowrap" data-id="element-563">
                        Step {index + 1} <br className="hidden md:block" data-id="element-564" />
                        <span className="text-gray-300" data-id="element-565">{step}</span>
                      </span>
                    </motion.div>)}
                </div>
              </div>

              <div className="mt-10 pt-6 flex justify-end relative z-10" data-id="element-566">
                <a href="#" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors font-medium group/link" data-id="element-567">
                  Learn more about {activeIndustry.name}
                  <ArrowRight className="w-4 h-4 text-brand-orange group-hover/link:translate-x-1 transition-transform" data-id="element-568" />
                </a>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>;
}